import { FieldValues, useForm } from 'react-hook-form';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { submitFeedback } from '@app/services/feedback';

interface ThumbnailItem {
  url: string;
  type: 'image' | 'video';
  file: File;
}

export const useLeaveFeedback = () => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [thumbnails, setThumbnails] = useState<ThumbnailItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYouCard, setShowThankYouCard] = useState(false);

  const leaveFeedbackForm = useForm();

  const handleImageThumbnail = (file: File) => {
    const newThumbnail = URL.createObjectURL(file);
    const fileType = file.type.startsWith('video/') ? 'video' : 'image';
    
    setUploadedFiles((prev) => [...prev, file]);
    setThumbnails((prev) => [...prev, { url: newThumbnail, type: fileType, file }]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || !selectedFiles?.length) {
      return;
    }

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/quicktime',
      'video/mov',
    ];
    
    const invalidFiles = Array.from(selectedFiles).filter(
      (file) => !allowedTypes.includes(file.type)
    );
    
    if (invalidFiles.length > 0) {
      e.target.value = '';
      toast.error('Invalid file format. Only JPEG, PNG, GIF, WebP images and MP4, MOV videos are allowed.');
      return;
    }

    // Check file size (max 5MB per file)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = Array.from(selectedFiles).filter(
      (file) => file.size > maxSize
    );
    
    if (oversizedFiles.length > 0) {
      e.target.value = '';
      toast.error('File size too large. Maximum size is 5MB per file.');
      return;
    }

    Array.from(selectedFiles).forEach((file) => {
      handleImageThumbnail(file);
    });
    
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });

    setThumbnails((prev) => {
      const newThumbnails = [...prev];
      URL.revokeObjectURL(newThumbnails[index].url);
      newThumbnails.splice(index, 1);
      return newThumbnails;
    });
  };

  const handleTagSelection = (tag: string) => {
    const currentTags = leaveFeedbackForm.watch('tags') ?? [];
    if (currentTags.includes(tag)) {
      leaveFeedbackForm.setValue(
        'tags',
        currentTags.filter((t: string) => t !== tag)
      );
    } else {
      leaveFeedbackForm.setValue('tags', [...currentTags, tag]);
    }
  };





  const handleSubmit = async (values: FieldValues) => {
    if (!values.feedback || !values.tags || values.tags.length === 0) {
      toast.error('Please provide feedback and select at least one tag');
      return;
    }

    setIsSubmitting(true);

    try {
      const feedbackData = {
        description: values.feedback,
        tags: values.tags,
        mediaFiles: uploadedFiles,
      };

      await submitFeedback(feedbackData);
      
      toast.success('Feedback submitted successfully!');
      setShowThankYouCard(true);
      
      // Reset form
      leaveFeedbackForm.reset();
      setUploadedFiles([]);
      setThumbnails([]);
      
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      const errorMessage = error?.data?.message || error?.message || 'An error occurred while submitting feedback';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    leaveFeedbackForm,
    handleChange,
    thumbnails,
    removeFile,
    handleSubmit,
    handleTagSelection,
    isSubmitting,
    showThankYouCard,
  };
};
